import { useMutation, useSuspenseQuery } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { Container } from "@roqua/components";
import { Section } from "@roqua/components/sections";
import { FieldLegend, FieldSet } from "@roqua/components/ui/field";

import { PreferencesQuery, ProfessionalUpdatePersonalData } from "@/features/Preferences/queries";
import { ProfessionalLocaleEnum } from "@/generated/gql/graphql";
import { useAppForm } from "@/hooks/useAppForm";
import { useTranslation } from "@/i18n/client";
import { useSessionContext } from "@/session";

export const Route = createFileRoute("/$organizationKey/$dossierId/preferences")({
  component: RouteComponent,
});

const formSchema = z.object({
  firstname: z.string().nullish(),
  lastname: z.string().nullish(),
  email: z.union([z.email(), z.literal("")]).transform((e) => (e && e.length > 0 ? e : "")),
  phoneCell: z.string().nullish(),
  locale: z.union([z.enum([ProfessionalLocaleEnum.Nl, ProfessionalLocaleEnum.En]), z.undefined()]),
  notifyByMailAfterFilloutRequest: z.boolean(),
});

function RouteComponent() {
  const { t } = useTranslation("Preferences");
  const { organizationKey, user } = useSessionContext();
  const { data } = useSuspenseQuery(PreferencesQuery, {
    variables: { organizationKey, professionalId: user.id },
  });

  const professional = data.organization.professional;

  const [mutate] = useMutation(ProfessionalUpdatePersonalData);

  const form = useAppForm({
    defaultValues: {
      firstname: professional.firstname ?? "",
      lastname: professional.lastname ?? "",
      email: professional.email ?? "",
      phoneCell: professional.phoneCell ?? "",
      locale: professional.locale ?? undefined,
      notifyByMailAfterFilloutRequest: professional.notifyByMailAfterFilloutRequest ?? false,
    } as z.infer<typeof formSchema>,
    validators: {
      onBlur: formSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const professional = formSchema.parse(value);

      const promise = mutate({
        variables: {
          professionalId: user.id,
          preferences: {
            locale: professional.locale,
            notifyByMailAfterFilloutRequest: professional.notifyByMailAfterFilloutRequest,
          },
          firstname: professional.firstname,
          lastname: professional.lastname,
          email: professional.email,
          phoneCell: professional.phoneCell,
        },
      });

      toast.promise(promise, {
        loading: t("common:changes_saving"),
        success: t("common:changes_saved"),
        error: t("common:changes_error"),
      });

      await promise;
      formApi.reset();
    },
  });

  return (
    <>
      <Section variant="header">
        <div className="flex items-center gap-2">
          <header className="flex-1 text-2xl">{t("header")}</header>
        </div>
      </Section>
      <Section variant="content">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-8"
        >
          <Container>
            <FieldSet>
              <FieldLegend>{t("personalInformation")}</FieldLegend>

              <form.AppField name="firstname">
                {(field) => <field.TextField label={t("professional.firstName")} />}
              </form.AppField>

              <form.AppField name="lastname">
                {(field) => <field.TextField label={t("professional.lastName")} />}
              </form.AppField>

              <form.AppField name="email">
                {(field) => <field.TextField label={t("professional.email")} />}
              </form.AppField>

              <form.AppField name="phoneCell">
                {(field) => <field.TextField label={t("professional.phoneCell")} />}
              </form.AppField>
            </FieldSet>
          </Container>

          <Container>
            <FieldSet>
              <FieldLegend>{t("preferences")}</FieldLegend>

              <form.AppField name="locale">
                {(field) => (
                  <field.SelectField
                    label={t("professional.language")}
                    placeholder="Select"
                    options={[
                      { label: "Nederlands", value: ProfessionalLocaleEnum.Nl },
                      { label: "English", value: ProfessionalLocaleEnum.En },
                    ]}
                  />
                )}
              </form.AppField>

              <span
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed
              peer-disabled:opacity-70"
              >
                {t("mailPreferences")}
              </span>
              <form.AppField name="notifyByMailAfterFilloutRequest">
                {(field) => <field.CheckboxField label={t("professional.notifyByMail")} />}
              </form.AppField>
            </FieldSet>
          </Container>

          <form.AppForm>
            <form.SubmitButton />
          </form.AppForm>
        </form>
      </Section>
    </>
  );
}
